import Immutable from 'immutable'
import React, { SyntheticEvent, useCallback } from 'react'

import styled from '@emotion/styled'
import {
  Box,
  Display,
  DisplayPosition,
  FlexDirection,
  JustifyContent,
  Space,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import { CookingTimeText } from '../CookingTimeText'
import { RecipeTilePurchaseInfo } from '../RecipeTilePurchaseInfo'
import { TileImage } from '../TileImage/TileImage'
import { Title } from '../Title'
import {
  cssAddRecipeButtonContainer,
  cssRecipeTagHolder,
  cssRecipeTile,
  cssRecipeTileContainer,
  cssRecipeTileIsFineDineIn,
} from './styles'
import { AddRecipeButton } from '../AddRecipeButton'
import { DietaryLabels } from '../DietaryLabels'
import { LabelWrapper } from '../Label/LabelWrapper'
import { SoldOutOverlay } from '../SoldOutOverlay'
import { SwapAlternativeOptions } from '../SwapAlternativeOptions/SwapAlternativeOptions'
import { getOverlayColor } from '../../utils/getOverlayColor'
import { CaloriesText } from '../Calories'
import { MenuApiPreferencesResponse } from '@library/menu-service'
import { Recipe } from '../../model/recipe'
import { AlternativeOptions } from '../../model/alternativeOptions/alternativeOptions'

const OuterContainer = styled.div(cssRecipeTile)

const TileContainer = styled.div((props: any) => ({
  ...cssRecipeTileContainer,
  ...(props.isFineDineIn ? cssRecipeTileIsFineDineIn : {}),
}))

const RecipeTagContainer = styled.span(() => ({
  ...cssRecipeTagHolder,
}))

const AddRecipeButtonContainer = styled.span((props: any) => ({
  ...cssAddRecipeButtonContainer,
  ...(props.isFineDineIn ? cssRecipeTileIsFineDineIn : {}),
}))

const ImageContainer = withCitrusTheme(Box, () => ({
  minHeight: '200px',
  maxHeight: '270px',
  position: 'relative',
  overflow: 'hidden',
}))

export type RecipeTileProps = {
  recipe: Recipe
  currentCollectionId: string
  onClick: (recipeId: string) => void
  menuPreferences?: MenuApiPreferencesResponse
  setBrowserCTAVisible: () => void
  numPortions: number
  canAddRecipes: boolean
  addRecipe: (recipeId: string) => void
  removeRecipe: (recipeId: string) => void
  reachedLimit: boolean
  isRecipeInBasket: (recipeId: string) => boolean
  isRecipeOutOfStock: (recipeId: string) => boolean
  selectVariant: ({
    variantId,
    variantOutOfStock,
    originalRecipeId,
    recipeReference,
  }: {
    variantId: string
    variantOutOfStock: boolean
    originalRecipeId: string
    recipeReference: string
  }) => void
  alternativeOptions: AlternativeOptions
  recipeReference: string
  originalId: string
  dietaryPreferences: Immutable.List<string>
  isMenuPersonalisationEnabled: boolean
  lazy?: boolean
  insideOverflow?: boolean
}

export const RecipeTile = ({
  recipe,
  currentCollectionId: categoryId,
  onClick,
  menuPreferences,
  setBrowserCTAVisible,
  numPortions,
  canAddRecipes,
  addRecipe,
  removeRecipe,
  reachedLimit,
  isRecipeInBasket,
  isRecipeOutOfStock,
  selectVariant,
  alternativeOptions,
  recipeReference,
  originalId,
  dietaryPreferences,
  isMenuPersonalisationEnabled,
  lazy = true,
  insideOverflow = false,
}: RecipeTileProps) => {
  const { id: recipeId, uuid: recipeUuid, isFineDineIn } = recipe
  const isOutOfStock = isRecipeOutOfStock(recipeId)

  const selectRecipeVariant = useCallback(
    ({ variantId, variantOutOfStock }: { variantId: string; variantOutOfStock: boolean }) => {
      selectVariant({
        variantId,
        variantOutOfStock,
        originalRecipeId: originalId,
        recipeReference,
      })
    },
    [originalId, recipeReference, selectVariant],
  )

  // should never happen but caters for loading state
  if (categoryId === null) {
    return null
  }

  // alternative options include the recipe itself
  const hasAlternativeOptions = alternativeOptions.length > 1

  const handleOnClick = (e: SyntheticEvent) => {
    e.stopPropagation()
    onClick(recipeUuid)
  }

  const handleOnClickRecipeButtonContainer = (e: SyntheticEvent) => {
    // Prevents opening recipe details modal when clicking on disabled add button
    if (isOutOfStock) e.stopPropagation()
  }

  return (
    <OuterContainer
      role="button"
      tabIndex={0}
      data-testing={isOutOfStock ? 'menuRecipeOutOfStock' : 'menuRecipeViewDetails'}
      data-testing-id={recipeId}
      onClick={handleOnClick}
      onKeyPress={handleOnClick}
    >
      <TileContainer isFineDineIn={isFineDineIn}>
        <ImageContainer>
          <SoldOutOverlay isRecipeOutOfStock={isRecipeOutOfStock} recipe={recipe} />
          <TileImage lazy={lazy} recipe={recipe} insideOverflow={insideOverflow} />
          <RecipeTagContainer>
            <DietaryLabels recipe={recipe} />
          </RecipeTagContainer>
        </ImageContainer>

        <Box position={DisplayPosition.Relative} flexGrow={1} style={{ margin: '1rem' }}>
          <Box display={Display.Flex} paddingBottom={2}>
            <LabelWrapper
              isRecipeOutOfStock={isRecipeOutOfStock}
              recipe={recipe}
              dietaryPreferences={dietaryPreferences}
              isMenuPersonalisationEnabled={isMenuPersonalisationEnabled}
            />
            <AddRecipeButtonContainer
              isFineDineIn={isFineDineIn}
              isSoldOut={isOutOfStock}
              onClick={handleOnClickRecipeButtonContainer}
            >
              <AddRecipeButton
                recipeId={recipeId}
                setBrowserCTAVisible={setBrowserCTAVisible}
                canAddRecipes={canAddRecipes}
                addRecipe={addRecipe}
                removeRecipe={removeRecipe}
                reachedLimit={reachedLimit}
                isRecipeInBasket={isRecipeInBasket}
                isRecipeOutOfStock={isRecipeOutOfStock}
              />
            </AddRecipeButtonContainer>
          </Box>
          <Box
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            justifyContent={JustifyContent.SpaceBetween}
            height="100%"
            position={DisplayPosition.Relative}
          >
            <SoldOutOverlay
              backgroundColor={getOverlayColor(recipe)}
              isRecipeOutOfStock={isRecipeOutOfStock}
              recipe={recipe}
            />
            <Box>
              <Title title={recipe.title} />
              <Space size={2}></Space>
              <CookingTimeText numPortions={numPortions} recipe={recipe} />
              {(!menuPreferences || menuPreferences?.display_calories) && (
                <CaloriesText numPortions={numPortions} recipe={recipe} />
              )}
            </Box>
            <RecipeTilePurchaseInfo
              isRecipeOutOfStock={isRecipeOutOfStock}
              numPortions={numPortions}
              recipe={recipe}
            />
          </Box>
          {hasAlternativeOptions && (
            <Box position={DisplayPosition.Absolute} bottom="0" right="0" width="100%">
              <SwapAlternativeOptions
                recipeId={recipeId}
                categoryId={categoryId}
                recipe={recipe}
                selectVariant={selectRecipeVariant}
                alternativeOptions={alternativeOptions}
              />
            </Box>
          )}
        </Box>
      </TileContainer>
    </OuterContainer>
  )
}
